import { useState } from "react";
import { MessageCircle, X, History, Plus } from "lucide-react";
import { useChat } from "../hooks/useChat";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { toggleModuleSelection } from "../store/slices/moduleSelectionSlice";
import FloatingChatContent from "./FloatingChatContent";
import FloatingChatSidebar from "./FloatingChatSidebar";
import "./FloatingChat.css";
import { useLocation, matchPath } from "react-router-dom";
import { useTheme } from '@/App';

const FloatingChat = () => {
  const location = useLocation();
  const { isDark } = useTheme();
  // Ensure isDark is properly boolean
  const darkMode = Boolean(isDark);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [triggerNewSession, setTriggerNewSession] = useState(false);
  const { initializeDirectSession } = useChat();
  const dispatch = useAppDispatch();
  const notAllowedPaths = [
  "/login",
  "/"
];
const isAllowPage = notAllowedPaths?.some(path =>
  matchPath({ path, end: true }, location.pathname)
);
  const requireModuleSelection = useAppSelector(
    (state) => state.moduleSelection.requireModuleSelection
  );

  // Toggle chat window visibility
  const handleToggleChat = (e) => {
    if (e) e.stopPropagation();
    if (isMinimized) {
      setIsMinimized(false);
      setIsOpen(true);
    } else {
      setIsOpen(!isOpen);
    }
  };

  // Close chat
  const handleCloseChat = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    setIsMinimized(false);
  };

  // Show/hide chat sessions
  const handleToggleSessions = (e) => {
    e.stopPropagation();
    setShowSessions(!showSessions);
  };

  // Toggle module selection mode (optional control)
  const handleToggleModuleSelection = (e) => {
    e.stopPropagation();
    dispatch(toggleModuleSelection());
  };

  // Add a new session
  const handleAddSession = (e) => {
    e.stopPropagation();
    if (requireModuleSelection) {
      setTriggerNewSession(true); // open module selection flow
    } else {
      initializeDirectSession(); // start chat directly
    }
  };

  return (
    <>
      {!isAllowPage ? (
        <div className={`floating-chat-container ${darkMode ? 'dark' : ''}`}>
          {/* Floating Button (always visible when closed) */}
          {!isMinimized && (
            <button onClick={handleToggleChat} className="floating-button">
              {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
            </button>
          )}

          {/* Minimized Chat Button */}
          {isMinimized && (
            <button onClick={handleToggleChat} className="minimized-button">
              <MessageCircle size={20} />
              <span style={{ fontSize: "14px", fontWeight: "500" }}>Chat</span>
            </button>
          )}

          {/* Chat Popup Window */}
          {isOpen && (
            <div
              className="chat-popover"
              style={{
                width: showSessions
                  ? Math.min(800, window.innerWidth - 40) + "px"
                  : Math.min(500, window.innerWidth - 40) + "px",
                height: Math.min(700, window.innerHeight - 80) + "px",
              }}
            >
              {/* Chat Header */}
              <div className="chat-header">
                <div className="header-title">
                  <MessageCircle size={20} />
                  <span>AI Assistant</span>
                </div>
                <div className="header-controls">
                  <button
                    onClick={handleToggleSessions}
                    className={`control-button ${showSessions ? "active" : ""}`}
                    title={showSessions ? "Hide Sessions" : "Show Sessions"}
                  >
                    <History size={16} />
                  </button>
                  <button
                    onClick={handleAddSession}
                    className="control-button"
                    title={
                      requireModuleSelection
                        ? "Start New Chat (Select Module)"
                        : "Start New Session"
                    }
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    onClick={handleCloseChat}
                    className="control-button"
                    title="Close"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Chat Body */}
              <div className="chat-body">
                {showSessions && <FloatingChatSidebar />}
                <FloatingChatContent
                  triggerNewSession={triggerNewSession}
                  onNewSessionTriggered={() => setTriggerNewSession(false)}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        ""
      )}
    </>
  );
};

export default FloatingChat;
